import { useState, useEffect, useReducer } from 'react';
import { db } from '../firebase/config';
import { collection, addDoc, Timestamp, doc, setDoc, runTransaction } from 'firebase/firestore';
import {
    buildAppointmentSlots,
    buildDayOffId,
    buildDayOffSlots,
    hasValidAppointmentHours,
} from '../utilities/appointmentSlots';

const initialState = {
    loading: null,
    error: null
}

const insertReducer = (state, action) => {
    switch (action.type) {
        case "LOADING":
          return { loading: true, error: null };
        case "INSERTED_DOC":
          return { loading: false, error: null };
        case "ERROR":
          return { loading: false, error: action.payload };
        default:
          return state;
        }

}

export const useInsertDocument = (docCollection) => {

    const [response, dispatch] = useReducer(insertReducer, initialState);
    const [error, setError] = useState(false)

    // deal with memory leak
    const [cancelled, setCancelled] = useState(false);

    const checkCancelBeforeDispatch = (action) => {
        if (!cancelled) {
            dispatch(action);
        };
    };

    const setTransactionDocument = async (document, id) => {
        const slots = buildAppointmentSlots(document);

        if (!document.date || !document.professional || !hasValidAppointmentHours(document.hour)) {
            throw new Error("Selecione um horário válido.");
        }

        const createdAt = Timestamp.now();
        const transactionRef = doc(db, docCollection, id);
        const dayOffRef = doc(db, 'day-off', buildDayOffId(document));
        const legacyDayOffRef = doc(db, 'day-off', document.date);
        const slotRefs = slots.map((slot) => ({
            ...slot,
            ref: doc(db, 'slots', slot.id),
        }));

        await runTransaction(db, async (transaction) => {
            const dayOffSnapshot = await transaction.get(dayOffRef);
            const legacyDayOffSnapshot = await transaction.get(legacyDayOffRef);

            const legacyDayOffData = legacyDayOffSnapshot.exists()
                ? legacyDayOffSnapshot.data()
                : null;

            if (
                dayOffSnapshot.exists() ||
                legacyDayOffData?.professional === document.professional
            ) {
                throw new Error("Este barbeiro está de folga nesta data. Escolha outro barbeiro ou outra data.");
            }

            for (const slot of slotRefs) {
                const slotSnapshot = await transaction.get(slot.ref);

                if (slotSnapshot.exists() && slotSnapshot.data().transactionId !== id) {
                    const slotData = slotSnapshot.data();
                    const message = slotData.dayOff
                        ? "Este barbeiro está de folga nesta data. Escolha outro barbeiro ou outra data."
                        : "Este horário acabou de ser preenchido. Escolha outro horário.";

                    throw new Error(message);
                }
            }

            const newDocument = {
                ...document,
                hour: slots.map((slot) => slot.hour),
                reservedSlots: slots.map((slot) => slot.id),
                createdAt,
            };

            transaction.set(transactionRef, newDocument);

            slotRefs.forEach((slot) => {
                transaction.set(slot.ref, {
                    date: slot.date,
                    professional: slot.professional,
                    hour: slot.hour,
                    transactionId: id,
                    service: document.service || "",
                    uid: document.uid || null,
                    lunch: Boolean(document.lunch),
                    createdAt,
                });
            });
        });
    };

    const setDayOffDocument = async (document, id) => {
        if (!document.date || !document.professional) {
            throw new Error("Selecione data e barbeiro para agendar a folga.");
        }

        const createdAt = Timestamp.now();
        const dayOffRef = doc(db, docCollection, id);
        const slots = buildDayOffSlots(document);
        const slotRefs = slots.map((slot) => ({
            ...slot,
            ref: doc(db, 'slots', slot.id),
        }));

        await runTransaction(db, async (transaction) => {
            const dayOffSnapshot = await transaction.get(dayOffRef);

            if (dayOffSnapshot.exists()) {
                throw new Error("Esta folga já está agendada para este barbeiro.");
            }

            for (const slot of slotRefs) {
                const slotSnapshot = await transaction.get(slot.ref);

                if (slotSnapshot.exists()) {
                    throw new Error("Já existe agendamento ou almoço para este barbeiro nesta data.");
                }
            }

            const newDocument = {
                ...document,
                reservedSlots: slots.map((slot) => slot.id),
                createdAt,
            };

            transaction.set(dayOffRef, newDocument);

            slotRefs.forEach((slot) => {
                transaction.set(slot.ref, {
                    date: slot.date,
                    professional: slot.professional,
                    hour: slot.hour,
                    transactionId: id,
                    dayOffId: id,
                    dayOff: true,
                    service: "Folga",
                    uid: null,
                    lunch: false,
                    createdAt,
                });
            });
        });
    };

    const insertDocument = async (document) => {

        checkCancelBeforeDispatch({
            type: "LOADING",
        })
        setError(false)

        try {
            const newDocument = { ...document, createdAt: Timestamp.now() }

            const insertedDocument = await addDoc(
                collection(db, docCollection),
                newDocument
            )

            checkCancelBeforeDispatch({
                type: "INSERTED_DOC",
                payload: insertedDocument
            })

            return { success: true, document: insertedDocument };

        } catch (error) {

            console.log(error);
            setError(error.message)

            checkCancelBeforeDispatch({
                type: "ERROR",
                payload: error.message
            })

            return { success: false, error: error.message };

        }

    }
    const setDocument = async (document, id) => {

        checkCancelBeforeDispatch({
            type: "LOADING",
        })
        setError(false)

        try {
            let settedDocument;

            if (docCollection === 'transactions') {
                await setTransactionDocument(document, id);
            } else if (docCollection === 'day-off') {
                await setDayOffDocument(document, id);
            } else {
                const newDocument = { ...document, createdAt: Timestamp.now() }
                settedDocument = await setDoc(doc(db, docCollection, id), newDocument)
            }

            checkCancelBeforeDispatch({
                type: "INSERTED_DOC",
                payload: settedDocument
            })

            return { success: true, id };

        } catch (error) {

            setError(error.message)

            checkCancelBeforeDispatch({
                type: "ERROR",
                payload: error.message
            })

            return { success: false, error: error.message };

        }

    }

    useEffect(() => {
        return () => setCancelled(true);
    }, []);

    return { insertDocument, setDocument, response, error }
}
